import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  FlexPayOverTimeOverviewResultTypenames,
  type Mutation,
  type MutationSubmitFlexPayOverTimePlanArgs,
  type Query,
  SubmitFlexPayOverTimePlanResultTypenames,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useRouter,
} from 'expo-router';
import LoadingSequence from 'features/payLater/components/LoadingSequence/LoadingSequence';
import WhatCouldChangeBottomSheet from 'features/payLater/components/WhatCouldChangeBottomSheet/WhatCouldChangeBottomSheet';
import { payLaterSetUpAtom } from 'features/payLater/store/payLaterSetUpStore';
import { useAtomValue } from 'jotai';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import SubmitFlexPayOverTimePlan from 'services/graphql/mutations/SubmitFlexPayOverTimePlan';
import FetchFlexPayOverTimeOverview from 'services/graphql/queries/FetchFlexPayOverTimeOverview';
import PaymentPlanAgreement from 'shared/components/lending/paymentPlanReview/PaymentPlanAgreement/PaymentPlanAgreement';
import PaymentPlanAutopay from 'shared/components/lending/paymentPlanReview/PaymentPlanAutopay/PaymentPlanAutopay';
import PaymentPlanHeader from 'shared/components/lending/paymentPlanReview/PaymentPlanHeader/PaymentPlanHeader';
import PaymentPlanLineItems from 'shared/components/lending/paymentPlanReview/PaymentPlanLineItems/PaymentPlanLineItems';
import PaymentPlanReviewLayout from 'shared/components/lending/paymentPlanReview/PaymentPlanReviewLayout/PaymentPlanReviewLayout';
import PaymentScheduleTimeline from 'shared/components/lending/PaymentScheduleTimeline/PaymentScheduleTimeline';
import Button from 'shared/components/vxl/Button/Button';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import useCancellableQuery from 'shared/hooks/useCancellableQuery/useCancellableQuery';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import usePaymentPlanAccount from 'shared/hooks/usePaymentPlanAccount/usePaymentPlanAccount';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { useIdempotencyToken } from 'shared/utils/idempotencyTokens/idempotencyTokenUtils';

const PLAN_ACTIVATION_INTENT = 'pay-later-plan-activation';
// TODO JT-163173: Figma says "Unfinished after XX seconds". Confirm the value
// with design; 30 seconds matches the activation request timeout.
const LONG_WAIT_AFTER_MS = 30000;

const getActivePlans = (data: Query | undefined) => {
  const overview = data?.flexPayOverTimeOverview;
  return overview?.__typename ===
    FlexPayOverTimeOverviewResultTypenames.FlexPayOverTimeOverviewSuccess
    ? overview.activePlans
    : null;
};

const getActivePlanIds = (data: Query | undefined) =>
  getActivePlans(data)?.map((activePlan) => activePlan.id) ?? null;

const PlanReview = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('payLater', {
    keyPrefix: 'review',
  });
  const dismissToTab = useDismissToRootTab();
  const setUp = useAtomValue(payLaterSetUpAtom);
  // TODO JT-163173: the backend should set the initial AutoPay value; the
  // estimate response doesn't include it yet.
  const [isAutoPayEnabled, setIsAutoPayEnabled] = useState(true);
  const [isAgreed, setIsAgreed] = useState(false);
  const whatCouldChangeSheetRef = useRef<BottomSheetModal>(null);
  const { account, fetching: isAccountLoading } = usePaymentPlanAccount();
  const { idempotencyToken, rotateToken } = useIdempotencyToken(
    PLAN_ACTIVATION_INTENT,
  );
  const [isCheckingPlans, setIsCheckingPlans] = useState(false);
  const [fetchOverview] = useCancellableQuery<Query>(
    FetchFlexPayOverTimeOverview,
  );
  const [{ fetching: isActivating }, submitPlan] = useCancellableMutation<
    Mutation,
    MutationSubmitFlexPayOverTimePlanArgs
  >(SubmitFlexPayOverTimePlan);

  if (!setUp) {
    return <Redirect href="/pay-later/purchase-amount" />;
  }

  const { paymentPlan, purchaseAmount } = setUp;
  const { paymentPlanDetails } = paymentPlan;

  const handleWhatCouldChangePress = () => {
    whatCouldChangeSheetRef.current?.present();
  };

  const handleTruthInLendingPress = () => {
    // TODO JT-163173: open the TILA disclosure PDF. The estimate returns only
    // agreementId, so there's no document ID to fetch yet.
  };

  const handleFinishInBackground = () => {
    dismissToTab(Tabs.HOME);
  };

  const findActivatedPlan = async (baselinePlanIds: string[]) => {
    const overviewData = await fetchOverview({});
    const activePlans = getActivePlans(overviewData);
    return (
      activePlans?.find(
        (activePlan) => !baselinePlanIds.includes(activePlan.id),
      ) ?? null
    );
  };

  const handleConfirmPress = async () => {
    let baselinePlanIds;
    setIsCheckingPlans(true);
    try {
      const overviewData = await fetchOverview({});
      baselinePlanIds = getActivePlanIds(overviewData);
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
    }
    setIsCheckingPlans(false);

    // Without the plans from before activation, a timeout can't be
    // reconciled, so don't submit.
    if (!baselinePlanIds) {
      router.push('/pay-later/set-up-error');
      return;
    }

    let plan;
    try {
      const result = await submitPlan({
        idempotencyId: idempotencyToken,
        input: {
          transactionAmount: purchaseAmount,
          isAutoPayEnabled,
          registrationInfoId: paymentPlan.registrationInfoId,
        },
      });
      plan = result.data?.submitFlexPayOverTimePlan;
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
    }

    if (
      plan?.__typename ===
      SubmitFlexPayOverTimePlanResultTypenames.SubmitFlexPayOverTimePlanSuccess
    ) {
      // TODO JT-163173: the success screen shows the expiry from expiresAt,
      // but activation returns only planId and summary. Read expiresAt from
      // flexPayOverTimeOverview or show the summary instead.
      rotateToken();
      router.push('/pay-later/set-up-success');
      return;
    }

    if (
      plan?.__typename ===
      SubmitFlexPayOverTimePlanResultTypenames.SubmitFlexPayOverTimePlanError
    ) {
      rotateToken();
      router.push('/pay-later/set-up-error');
      return;
    }

    // The outcome is unknown (timeout or network error), so the plan may have
    // been armed. Check the overview before showing the error.
    setIsCheckingPlans(true);
    try {
      const activatedPlan = await findActivatedPlan(baselinePlanIds);
      setIsCheckingPlans(false);
      if (activatedPlan) {
        rotateToken();
        router.push({
          pathname: '/pay-later/set-up-success',
          params: { expiresAt: activatedPlan.expiresAt },
        });
        return;
      }
      router.push('/pay-later/set-up-error');
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
      setIsCheckingPlans(false);
      router.push('/pay-later/set-up-error');
    }
  };

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <PaymentPlanReviewLayout
        footer={
          <>
            <Button
              label={translatedText('confirm')}
              onPress={handleConfirmPress}
              type="PRIMARY"
              width="FULL"
              isDisabled={!isAgreed || isActivating || isCheckingPlans}
            />
            <Button
              label={translatedText('cancel')}
              onPress={router.back}
              type="SECONDARY"
              width="FULL"
            />
          </>
        }
      >
        <PaymentPlanHeader
          title={translatedText('title')}
          summary={paymentPlanDetails.summaryHeadline}
          summaryLink={{
            label: translatedText('whatCouldChange'),
            onPress: handleWhatCouldChangePress,
          }}
        />
        <PaymentPlanLineItems lineItems={paymentPlanDetails.lineItems} />
        <View style={styles.sectionDivider} />
        <PaymentPlanAutopay
          sectionTitle={translatedText('paymentMethod')}
          isEnabled={isAutoPayEnabled}
          isDisabled={isActivating}
          onChange={setIsAutoPayEnabled}
          isAccountLoading={isAccountLoading}
          account={account}
        />
        <PaymentScheduleTimeline
          title={paymentPlanDetails.paymentSchedule.title}
          description={translatedText('scheduleDescription')}
          items={paymentPlanDetails.paymentSchedule.scheduleItems.map(
            (item) => ({
              id: `${item.dueDate}-${item.amount.amount}`,
              label: item.label,
            }),
          )}
          disclosureText={paymentPlanDetails.paymentSchedule.disclosureText}
        />
        <View style={styles.sectionDivider} />
        {/* TODO JT-163173: show the Military Lending Act disclosure for covered
            borrowers once the contract flags coverage and design adds it. */}
        {/* TODO JT-163173: Figma's notice says the plan expires in 6 hours.
            Add the window once the contract returns it. */}
        <PaymentPlanAgreement
          isAgreed={isAgreed}
          onAgreementChange={() => setIsAgreed(!isAgreed)}
          documentLink={{
            label: translatedText('truthInLending'),
            onPress: handleTruthInLendingPress,
          }}
          notice={translatedText('expiryNotice')}
        />
      </PaymentPlanReviewLayout>
      <ScreenTransitionOverlay
        isLoading={isActivating || isCheckingPlans}
        LoadingComponent={
          <LoadingSequence
            longWaitAfterMs={LONG_WAIT_AFTER_MS}
            onFinishInBackground={handleFinishInBackground}
          />
        }
        ErrorComponent={null}
      />
      <WhatCouldChangeBottomSheet ref={whatCouldChangeSheetRef} />
    </View>
  );
};

const styles = StyleSheet.create({
  screen: { backgroundColor: theme.color.background.primary, flex: 1 },
  sectionDivider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
});

export default PlanReview;
